import React, { useMemo } from 'react';

import { Form, Field, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';
import { StringParam, useQueryParam } from 'use-query-params';

import FormSection from 'components/Layouts/Form/FormSection';
import LayoutWithFooter from 'components/Layouts/shared/LayoutWithFooter';
import ErrorScreen from 'components/shared/ErrorScreen';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import { GeneralPopoverPagePreview } from 'components/shared/GeneralPopoverPagePreview';
import Loader from 'components/shared/Loader';
import Text from 'components/shared/Typography/Text';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import {
  PAGE_TYPE_ABOUT_SAFARI,
  PAGE_TYPE_ABOUT_US,
  PAGE_TYPE_ACTIVITY,
  PAGE_TYPE_AREA,
  PAGE_TYPE_MISC,
  PAGE_TYPE_PRE_TRAVEL_INFO,
  PAGE_TYPE_PROPERTY,
} from 'constants/index';
import useElementBottomPosition, { SNACKBAR_POSITION } from 'hooks/useElementBottomPosition';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useScrollPageToTop } from 'hooks/useScrollPageToTop';
import useValidationSchema from 'hooks/useValidationSchema';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { useItinerariesItemContext } from 'utils/itineraries/context';
import {
  useItinerariesItemNextStepFormHandler,
  useItinerariesItemSaveHandler,
} from 'utils/itineraries/functions/builderUtils';

import ItinerariesItemActionButtons from '../../ItinerariesItemBuilder/ActionButtons';
import PageMultiselect from '../../Lookbook/GeneralInfo/PageMultiselect';
import { BuilderFooterButton } from '../../PageTypes/shared/BuilderFooterButton';
import ItinerarySearchFilterButton from '../../shared/SearchFilter';
import styles from './index.module.css';
import validationSchema from './validationSchema';

const pageTypesToSearch = [
  PAGE_TYPE_ACTIVITY,
  PAGE_TYPE_MISC,
  PAGE_TYPE_ABOUT_SAFARI,
  PAGE_TYPE_ABOUT_US,
  PAGE_TYPE_PRE_TRAVEL_INFO,
  PAGE_TYPE_PROPERTY,
  PAGE_TYPE_AREA,
];

const prepareSubmitValues = (values) => {
  return {
    ...values,

    commonPagesFirst: values.commonPagesFirst?.map((item) => ({ id: item.value })) ?? [],
    commonPagesSecond: values.commonPagesSecond?.map((item) => ({ id: item.value })) ?? [],
  };
};

function GeneralInfo() {
  const { isCreating } = useItinerariesItemContext();
  const [tourRequestId] = useQueryParam('tourRequestId', StringParam);

  if (isCreating && !tourRequestId) {
    return <ErrorScreen status="404" />;
  }

  return <GeneralInfoInner extraNextParams={isCreating ? { tourRequestId } : {}} />;
}

function GeneralInfoInner({ extraNextParams }) {
  useScrollPageToTop();
  useElementBottomPosition(SNACKBAR_POSITION);
  const validate = useValidationSchema(validationSchema);
  const handleNextStep = useItinerariesItemNextStepFormHandler();
  const handleSave = useItinerariesItemSaveHandler();
  const guestPortal = useSelector(itinerariesItemDataSelector);
  const [isSaving, setIsSaving] = React.useState(false);

  const initialValues = useMemo(
    () => ({
      ...guestPortal,
      commonPagesFirst: guestPortal.commonPagesFirst?.map((item) => ({ label: item.handle, value: item.id })),
      commonPagesSecond: guestPortal.commonPagesSecond?.map((item) => ({ label: item.handle, value: item.id })),
    }),
    [guestPortal],
  );

  return (
    <LayoutWithFooter>
      {({ footerComponent: FooterComponent }) => (
        <Form
          onSubmit={(values) => handleNextStep(prepareSubmitValues({ ...values, ...extraNextParams }))}
          validate={validate}
          initialValues={initialValues}
        >
          {({ handleSubmit, submitting, values, hasValidationErrors, form }) => {
            return (
              <form onSubmit={handleSubmit} autoComplete="nope" className={styles.form}>
                <UnsavedChangesFormWatcher />
                {submitting && <Loader isFillBlock />}

                <FormSection type="rounded">
                  <CoverSection />
                </FormSection>

                <FormSection type="rounded">
                  <IncludeSection />
                </FormSection>

                <FooterComponent>
                  <FormActions isRight>
                    <BuilderFooterButton onClick={form.submit} disabled={hasValidationErrors || submitting || isSaving}>
                      Next
                    </BuilderFooterButton>
                  </FormActions>
                </FooterComponent>

                <ItinerariesItemActionButtons
                  hasValidationErrors={hasValidationErrors}
                  submitting={submitting}
                  onSave={() => handleSave(prepareSubmitValues({ ...values, ...extraNextParams }))}
                  onIsSavingChange={setIsSaving}
                />
              </form>
            );
          }}
        </Form>
      )}
    </LayoutWithFooter>
  );
}

export default GeneralInfo;

const CoverSection = () => {
  const guestPortal = useSelector(itinerariesItemDataSelector);

  const guestPortalForPreview = (() => {
    return {
      ...guestPortal,
      title: guestPortal.title,
      travelDates: guestPortal.travelDates,
      preparedFor: guestPortal.preparedFor,
    };
  })();

  return (
    <>
      <div className={styles.coverTitleWrapper}>
        <div>
          <Text type="brandon" isStrong weight="500">
            Cover Page
          </Text>

          <Text type="brandon" size="regular" color="gray-600" className={styles.coverSubTitle}>
            This information will appear on the Cover Page.
          </Text>
        </div>

        <GeneralPopoverPagePreview proposal={guestPortalForPreview} />
      </div>

      <Field name="title" component={FormGroupAdapter} placeholder="Title*" noTransform />

      <Field
        name="notes"
        component={FormGroupAdapter}
        placeholder="Notes..."
        type="textarea"
        minRows={3}
        parse={(value) => value || null}
        noTransform
        className={styles.textArea}
        noMargin
      />
    </>
  );
};

const IncludeSection = () => {
  const { isTabletOrMobile } = useMediaBreakpoints();
  const { values } = useFormState();
  return (
    <>
      <div className={styles.includeTitleWrapper}>
        <Text type="brandon" isStrong weight="500">
          Include Information Pages
        </Text>

        <Text type="brandon" size="regular" color="gray-600" className={styles.coverSubTitle}>
          Select pages from your content library
        </Text>
      </div>

      <div className={styles.container}>
        <div className={styles.includeSectionSelectLabel}>
          <Text type="brandon" size="regular">
            Introduction
          </Text>
          &nbsp;
          <Text type="brandon" size="regular" color="gray-600">
            (Beginning of Guest Portal)
          </Text>
        </div>
        <Field
          name="filtersForIntroduction"
          component={() => (
            <ItinerarySearchFilterButton
              isMobile={isTabletOrMobile}
              filter={values?.filtersForIntroduction}
              filterName="Content Pages"
              filterKeyName="filtersForIntroduction"
              className={styles.filterContainer}
              values={values}
            />
          )}
        />
      </div>

      <Field
        component={FormGroupAdapter}
        customComponent={PageMultiselect}
        typesToSearch={pageTypesToSearch}
        additionalQueryParams={values?.filtersForIntroduction?.data}
        defaultOptions={values.commonPagesFirst}
        className={styles.includeFormGroup}
        name="commonPagesFirst"
        placeholder="Select pages"
        noTransform
      />

      <div className={styles.container}>
        <div className={styles.includeSectionSelectLabel}>
          <Text type="brandon" size="regular">
            Information Pages
          </Text>
          &nbsp;
          <Text type="brandon" size="regular" color="gray-600">
            (End of Guest Portal)
          </Text>
        </div>
        <Field
          name="filtersForInformationPages"
          component={() => (
            <ItinerarySearchFilterButton
              isMobile={isTabletOrMobile}
              filter={values?.filtersForInformationPages}
              filterName="Content Pages"
              filterKeyName="filtersForInformationPages"
              className={styles.filterContainer}
              values={values}
            />
          )}
        />
      </div>

      <Field
        component={FormGroupAdapter}
        customComponent={PageMultiselect}
        typesToSearch={pageTypesToSearch}
        additionalQueryParams={values?.filtersForInformationPages?.data}
        defaultOptions={values.commonPagesSecond}
        name="commonPagesSecond"
        placeholder="Select pages"
        noTransform
        filter={values?.filtersForInformationPages}
        filterName="Content Pages"
        filterKeyName="filtersForInformationPages"
        noMargin
      />
    </>
  );
};
